import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const computerScienceUnit4Notes: UnitNotes = {
  unitId: "unit-4",
  unitName: "Unit 4: Data Collections",
  examWeight: "30–40%",
  focus: "Arrays, ArrayLists, 2D Arrays, Standard Algorithms, Searching, Sorting, and Recursion.",
  sections: [
    {
      id: "topic-4-1",
      title: "Topic 4.1: Ethical and Social Issues Around Data Collection",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌐 The Impact of Computing</h3>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-red-50 to-rose-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🛡️ Privacy Concerns</h4>
                <p className="text-sm text-slate-700 mt-1">
                  Collection of <strong>PII (Personally Identifiable Information)</strong> puts user privacy at risk. 
                </p>
                <p className="text-xs text-slate-600 mt-2 italic"><strong>Data Aggregation:</strong> Combining datasets can reveal sensitive details (location, habits, health) even if individual data points seem harmless.</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-slate-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">⚙️ System Reliability</h4>
                <p className="text-sm text-slate-700 mt-1">Programs must work under defined conditions. Programmers must test boundary cases to ensure reliability.</p>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">⚖️ Legal and Intellectual Property</h4>
                <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700 mt-2">
                  <li><strong>Open Source:</strong> Code published freely for use and modification.</li>
                  <li><strong>Proprietary/Closed Source:</strong> Code that requires permission or purchase to use.</li>
                  <li><strong>Plagiarism:</strong> Copying code without attribution or permission is an ethical and legal violation.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤖 Algorithmic Bias</h3>
            <KeyTerm 
              term="Algorithmic Bias" 
              definition="Systemic and repeated errors in a computer system that create unfair outcomes, such as privileging one arbitrary group of users over others."
            />
            <div className="mt-3 p-3 bg-slate-50 border-l-4 border-slate-400 rounded">
              <p className="text-sm text-slate-700"><strong>Source:</strong> Often stems from the <strong>data set</strong> used to train the algorithm. If data is unrepresentative, the output will be biased, regardless of the code logic.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-2",
      title: "Topic 4.2: Introduction to Using Data Sets",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Data Representation & Processing</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Data is conceptualized as a collection of values handled via data structures. To analyze data, we iterate (loop) through the collection to perform three main tasks:
            </p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 text-center">
                <span className="text-2xl mb-1 block">🧼</span>
                <h5 className="font-bold text-indigo-900">Clean</h5>
                <p className="text-xs text-slate-600">Remove errors</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
                <span className="text-2xl mb-1 block">🔍</span>
                <h5 className="font-bold text-blue-900">Filter</h5>
                <p className="text-xs text-slate-600">Find specific subsets</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 text-center">
                <span className="text-2xl mb-1 block">⚙️</span>
                <h5 className="font-bold text-purple-900">Transform</h5>
                <p className="text-xs text-slate-600">Calculations/Analysis</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-3",
      title: "Topic 4.3: Array Creation and Access",
      content: (
        <div className="space-y-6">
          <section>
            <KeyTerm 
              term="Array" 
              definition="A data structure used to store multiple values of the same type (primitive or object references). Arrays are static: size is fixed upon creation."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Creation & Initialization</h3>
            <div className="space-y-4">
              <div className="bg-slate-900 rounded-lg p-4">
                <h5 className="text-slate-400 text-xs font-mono mb-2">// 1. Using new (Default Values)</h5>
                <code className="text-blue-300 text-sm">int[] numbers = new int[5];</code>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-50 p-3 rounded border text-center">
                  <span className="text-xs font-bold block text-slate-500">int</span>
                  <InlineMath math="\rightarrow 0" />
                </div>
                <div className="bg-slate-50 p-3 rounded border text-center">
                  <span className="text-xs font-bold block text-slate-500">double</span>
                  <InlineMath math="\rightarrow 0.0" />
                </div>
                <div className="bg-slate-50 p-3 rounded border text-center">
                  <span className="text-xs font-bold block text-slate-500">boolean</span>
                  <InlineMath math="\rightarrow false" />
                </div>
                <div className="bg-slate-50 p-3 rounded border text-center">
                  <span className="text-xs font-bold block text-slate-500">Objects</span>
                  <InlineMath math="\rightarrow null" />
                </div>
              </div>

              <div className="bg-slate-900 rounded-lg p-4">
                <h5 className="text-slate-400 text-xs font-mono mb-2">// 2. Initializer Lists</h5>
                <code className="text-emerald-300 text-sm">String[] names = {"{ \"Alice\", \"Bob\", \"Charlie\" }"};</code>
                <p className="text-slate-500 text-xs mt-2 italic">// Size is 3; indices 0, 1, 2.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Accessing Elements</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded border border-amber-200 text-sm">
                <span className="font-bold text-amber-900">Indexing:</span>
                <p className="text-slate-700">0-based indexing. First element is at <code>[0]</code>.</p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded border border-blue-200 text-sm">
                <span className="font-bold text-blue-900">Length:</span>
                <p className="text-slate-700">Use <code>arrayName.length</code> (variable, no parentheses).</p>
              </div>
              <ImportantNote>
                <strong>Bounds Error:</strong> Accessing index <InlineMath math="< 0" /> or <InlineMath math="\ge \text{length}" /> throws an <code>ArrayIndexOutOfBoundsException</code>.
              </ImportantNote>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-4",
      title: "Topic 4.4: Array Traversals",
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="font-bold text-slate-800">1. Standard for Loop</h4>
              <p className="text-sm text-slate-600 italic">Best for modifying elements or using the index.</p>
              <div className="bg-slate-900 rounded-lg p-4 font-mono text-xs">
                <pre className="text-blue-300">
{`for (int i = 0; i < arr.length; i++) {
    arr[i] = arr[i] + 5; 
}`}
                </pre>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="font-bold text-slate-800">2. Enhanced for Loop</h4>
              <p className="text-sm text-slate-600 italic">Read-only. Cleaner syntax.</p>
              <div className="bg-slate-900 rounded-lg p-4 font-mono text-xs">
                <pre className="text-emerald-300">
{`for (int num : arr) {
    System.out.println(num);
}`}
                </pre>
              </div>
            </div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h5 className="font-bold text-red-900 mb-1">Enhanced Loop Limitation:</h5>
            <p className="text-sm text-slate-700">You <strong>cannot</strong> modify primitives inside the array using the temporary variable in an enhanced for-loop.</p>
          </div>
        </div>
      )
    },
    {
      id: "topic-4-5",
      title: "Topic 4.5: Implementing Array Algorithms",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Standard Algorithms</h3>
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg border">
                <h5 className="font-bold text-slate-900 mb-2">Find Minimum/Maximum</h5>
                <p className="text-sm text-slate-700 mb-3">Initialize to the <strong>first element</strong> (<code>arr[0]</code>), not 0.</p>
                <div className="bg-slate-900 p-3 rounded font-mono text-xs text-blue-300">
                  {`int min = arr[0];
for(int val : arr) {
    if (val < min) min = val;
}`}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div className="p-4 bg-white border rounded-lg">
                  <h6 className="font-bold text-slate-800">Sum/Average</h6>
                  <p className="text-xs text-slate-600 mt-1">Sum / <code>arr.length</code>. <strong>Warning:</strong> Watch out for integer division!</p>
                </div>
                <div className="p-4 bg-white border rounded-lg">
                  <h6 className="font-bold text-slate-800">Linear Search</h6>
                  <p className="text-xs text-slate-600 mt-1">If <code>target</code> is found, return <code>true</code> or the index; else return <code>false</code> or -1.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-6",
      title: "Topic 4.6: Using Text Files",
      content: (
        <div className="space-y-6">
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h4 className="font-bold text-slate-800 mb-3">Key Implementation</h4>
            <div className="bg-slate-900 rounded p-4 font-mono text-xs text-blue-300 overflow-x-auto">
<pre>{`File myFile = new File("data.txt");
Scanner scanner = new Scanner(myFile);
while (scanner.hasNext()) {
    String word = scanner.next();
}
scanner.close();`}</pre>
            </div>
          </div>
          <ImportantNote>
            <strong>The Scanner Pitfall:</strong> <code>nextInt()</code> reads the number but leaves the newline character (<code>\n</code>) in the buffer. A subsequent <code>nextLine()</code> will return an empty string. 
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-4-7",
      title: "Topic 4.7: Wrapper Classes",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700">ArrayLists only hold Objects. Wrapper classes "wrap" primitives.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-bold text-purple-900">Autoboxing</h4>
              <p className="text-xs text-slate-700 mt-1">Primitive <InlineMath math="\rightarrow" /> Wrapper</p>
              <code className="text-xs font-mono block mt-2">Integer num = 5;</code>
            </div>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
              <h4 className="font-bold text-indigo-900">Unboxing</h4>
              <p className="text-xs text-slate-700 mt-1">Wrapper <InlineMath math="\rightarrow" /> Primitive</p>
              <code className="text-xs font-mono block mt-2">int val = num;</code>
            </div>
          </div>
          <div className="p-3 bg-slate-50 border-l-4 border-slate-400 text-sm">
            <strong>Immutability:</strong> Wrapper objects are immutable. You cannot change the value; you must create a new object.
          </div>
        </div>
      )
    },
    {
      id: "topic-4-8",
      title: "Topic 4.8: ArrayList Methods",
      content: (
        <div className="space-y-6">
          <KeyTerm term="ArrayList" definition="A dynamic data structure from java.util that resizes automatically." />
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-slate-200 rounded-lg">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">Method</th>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-sm">
                <tr><td className="px-4 py-2 font-mono">int size()</td><td className="px-4 py-2">Returns number of elements.</td></tr>
                <tr><td className="px-4 py-2 font-mono">boolean add(E obj)</td><td className="px-4 py-2">Appends to end. Returns true.</td></tr>
                <tr><td className="px-4 py-2 font-mono">void add(int i, E obj)</td><td className="px-4 py-2">Inserts at <code>i</code>; <strong>shifts right</strong>.</td></tr>
                <tr><td className="px-4 py-2 font-mono">E get(int i)</td><td className="px-4 py-2">Returns element at <code>i</code>.</td></tr>
                <tr><td className="px-4 py-2 font-mono">E set(int i, E obj)</td><td className="px-4 py-2">Replaces at <code>i</code>; <strong>returns OLD element</strong>.</td></tr>
                <tr><td className="px-4 py-2 font-mono">E remove(int i)</td><td className="px-4 py-2">Removes at <code>i</code>; <strong>shifts left</strong>. Returns removed element.</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: "topic-4-9",
      title: "Topic 4.9: ArrayList Traversals",
      content: (
        <div className="space-y-6">
          <section>
            <h4 className="font-bold text-slate-800 mb-2">Standard Loop & Removal Pitfall</h4>
            <p className="text-sm text-slate-700 mb-3">When removing at index <code>i</code>, elements shift left. If the loop increments <code>i++</code>, you skip an element.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="p-3 bg-green-50 border border-green-200 rounded text-xs">
                <p className="font-bold text-green-900 mb-1">✅ Fix 1: Decrement</p>
                <code>{`list.remove(i); i--;`}</code>
              </div>
              <div className="p-3 bg-green-50 border border-green-200 rounded text-xs">
                <p className="font-bold text-green-900 mb-1">✅ Fix 2: Backwards</p>
                <code>{`for(int i = size-1; i >= 0; i--)`}</code>
              </div>
            </div>
          </section>
          <ImportantNote>
            <strong>Enhanced For Loop:</strong> You <strong>cannot</strong> add or remove elements during traversal. Doing so throws a <code>ConcurrentModificationException</code>.
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-4-10",
      title: "Topic 4.10: Implementing ArrayList Algorithms",
      content: (
        <div className="space-y-4">
          <p className="text-sm text-slate-700">ArrayList logic matches Arrays, but use method calls:</p>
          <div className="bg-slate-50 p-4 rounded-lg border grid grid-cols-2 gap-4 text-xs font-mono">
            <div>
              <p className="text-slate-400 mb-1">// Array</p>
              <p>arr[i]</p>
              <p>arr[i] = val</p>
              <p>arr.length</p>
            </div>
            <div>
              <p className="text-slate-400 mb-1">// ArrayList</p>
              <p>list.get(i)</p>
              <p>list.set(i, val)</p>
              <p>list.size()</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "topic-4-11",
      title: "Topic 4.11: 2D Array Creation and Access",
      content: (
        <div className="space-y-4">
          <p className="text-sm text-slate-700">A 2D array is an "array of arrays," representing a grid.</p>
          <div className="bg-slate-900 p-4 rounded-lg font-mono text-xs text-blue-300">
            {`int[][] matrix = new int[3][4]; // 3 Rows, 4 Cols
int[][] grid = { {1, 2}, {3, 4}, {5, 6} };

int rows = matrix.length;
int cols = matrix[0].length;`}
          </div>
          <p className="text-xs text-slate-500">Accessing: <code>arr[row][col]</code></p>
        </div>
      )
    },
    {
      id: "topic-4-12",
      title: "Topic 4.12: 2D Array Traversals",
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-white border border-slate-200 rounded-lg">
              <h5 className="font-bold text-slate-800">Row-Major (Standard)</h5>
              <p className="text-xs text-slate-600 mb-2">Left to right, then top to bottom.</p>
              <code className="text-[10px] block bg-slate-50 p-2">Outer Loop: r, Inner Loop: c</code>
            </div>
            <div className="p-4 bg-white border border-slate-200 rounded-lg">
              <h5 className="font-bold text-slate-800">Column-Major</h5>
              <p className="text-xs text-slate-600 mb-2">Top to bottom, then left to right.</p>
              <code className="text-[10px] block bg-slate-50 p-2">Outer Loop: c, Inner Loop: r</code>
            </div>
          </div>
          <div className="bg-slate-900 p-4 rounded-lg">
            <h5 className="text-white text-xs font-bold mb-2">Enhanced For Loop (2D)</h5>
            <pre className="text-emerald-300 text-[10px]">
{`for (int[] row : matrix) {
    for (int num : row) {
        System.out.print(num);
    }
}`}
            </pre>
          </div>
        </div>
      )
    },
    {
      id: "topic-4-13",
      title: "Topic 4.13: Implementing 2D Array Algorithms",
      content: (
        <div className="grid md:grid-cols-3 gap-3">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded text-center">
            <h6 className="font-bold text-blue-900 text-sm">Sum Row</h6>
            <p className="text-[10px] text-slate-600">Fix row index, loop columns.</p>
          </div>
          <div className="p-3 bg-indigo-50 border border-indigo-200 rounded text-center">
            <h6 className="font-bold text-indigo-900 text-sm">Sum Col</h6>
            <p className="text-[10px] text-slate-600">Fix column index, loop rows.</p>
          </div>
          <div className="p-3 bg-purple-50 border border-purple-200 rounded text-center">
            <h6 className="font-bold text-purple-900 text-sm">Diagonal</h6>
            <p className="text-[10px] text-slate-600">Access where <code>row == col</code>.</p>
          </div>
        </div>
      )
    },
    {
      id: "topic-4-14",
      title: "Topic 4.14: Searching Algorithms",
      content: (
        <div className="space-y-6">
          <section>
            <h4 className="font-bold text-slate-800 border-b pb-1 mb-3">Linear Search</h4>
            <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
              <li><strong>Precondition:</strong> None (Unsorted data is fine).</li>
              <li><strong>Efficiency:</strong> Worst case <InlineMath math="N" />.</li>
            </ul>
          </section>
          <section>
            <h4 className="font-bold text-slate-800 border-b pb-1 mb-3">Binary Search</h4>
            <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
              <li><strong>Method:</strong> Divide and Conquer.</li>
              <li><strong>Precondition:</strong> <strong>Data MUST be sorted.</strong></li>
              <li><strong>Efficiency:</strong> Worst case <InlineMath math="\log_2 N" />.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-15",
      title: "Topic 4.15: Sorting Algorithms",
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
              <h5 className="font-bold text-blue-900">1. Selection Sort</h5>
              <p className="text-xs text-slate-700 mt-1">Find smallest element in unsorted portion and <strong>swap</strong> it with the front. After <InlineMath math="k" /> passes, first <InlineMath math="k" /> elements are sorted.</p>
            </div>
            <div className="bg-gradient-to-r from-slate-50 to-emerald-50 p-4 rounded-lg border-l-4 border-emerald-400">
              <h5 className="font-bold text-emerald-900">2. Insertion Sort</h5>
              <p className="text-xs text-slate-700 mt-1">Take first unsorted element and "insert" it into relative position by <strong>shifting</strong> larger elements right. Like sorting a hand of cards.</p>
            </div>
            <div className="bg-gradient-to-r from-slate-50 to-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
              <h5 className="font-bold text-purple-900">3. Merge Sort (Recursive)</h5>
              <p className="text-xs text-slate-700 mt-1">Recursive divide into size-1 arrays, then <strong>merge</strong> back. Efficiency: <InlineMath math="N \log N" />. Uses a helper array.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "topic-4-16",
      title: "Topic 4.16: Recursion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Anatomy of Recursion</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-red-50 p-4 rounded border border-red-200">
                <h5 className="font-bold text-red-900">Base Case</h5>
                <p className="text-xs text-slate-700 mt-1">Stops the recursion. Missing this leads to a <code>StackOverflowError</code>.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded border border-blue-200">
                <h5 className="font-bold text-blue-900">Recursive Step</h5>
                <p className="text-xs text-slate-700 mt-1">Method calls itself with modified arguments moving toward base case.</p>
              </div>
            </div>
          </section>
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Tracing (The Stack)</h3>
            <div className="p-4 bg-slate-900 rounded-lg">
              <p className="text-xs text-slate-400 italic mb-2">// Trace for fact(3)</p>
              <ul className="text-[10px] font-mono space-y-1">
                <li className="text-blue-300">fact(3) calls 3 * fact(2)</li>
                <li className="text-blue-300">fact(2) calls 2 * fact(1)</li>
                <li className="text-emerald-400">fact(1) returns 1 (Base Case)</li>
                <li className="text-white">...Resumes: 2 * 1 returns 2, then 3 * 2 returns 6</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-17",
      title: "Topic 4.17: Recursive Searching and Sorting",
      content: (
        <div className="space-y-6">
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h4 className="font-bold text-slate-800 mb-2">Recursive Binary Search</h4>
            <p className="text-xs text-slate-700 mb-2">Uses <code>low</code> and <code>high</code> indices.</p>
            <ul className="list-disc pl-5 text-xs text-slate-600 space-y-1">
              <li><strong>Base 1:</strong> <code>low &gt; high</code> (Target not found).</li>
              <li><strong>Base 2:</strong> <code>arr[mid] == target</code> (Target found).</li>
              <li><strong>Step:</strong> Recurse on <code>(low, mid - 1)</code> or <code>(mid + 1, high)</code>.</li>
            </ul>
          </div>
          <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
            <h4 className="font-bold text-indigo-900 mb-1">Merge Sort Implementation</h4>
            <p className="text-sm text-slate-700">Inherently recursive. The complex <strong>merge</strong> step iterates through two sorted subarrays, picking the smaller value for the combined array.</p>
          </div>
        </div>
      )
    }
  ]
};

export default computerScienceUnit4Notes;